import { useState, useMemo, useEffect } from 'react'
import { type ExcelColumnDef, type SortRule, getColVal, getColKey, getColHeader } from './types'

interface FilterPopoverProps<T> {
  column: ExcelColumnDef<T>
  columns: ExcelColumnDef<T>[]
  allRows: T[]
  currentExcluded: string[]
  sort: SortRule[]
  anchorRect: DOMRect
  onClose: () => void
  onApplyFilter: (excluded: string[]) => void
  onUpdateSort: (newSort: SortRule[]) => void
}

export function ExcelGridFilterPopover<T>({
  column,
  columns,
  allRows,
  currentExcluded,
  sort,
  anchorRect,
  onClose,
  onApplyFilter,
  onUpdateSort,
}: FilterPopoverProps<T>) {
  // Extract all distinct values
  const uniqueValues = useMemo(() => {
    const vals = new Set<string>()
    for (const row of allRows) {
      vals.add(String(getColVal(column, row) ?? ''))
    }
    return Array.from(vals).sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }))
  }, [allRows, column])

  const [excludedSet, setExcludedSet] = useState<Set<string>>(() => new Set(currentExcluded))
  const [filterSearch, setFilterSearch] = useState('')

  // Filter values list by search
  const visibleValues = useMemo(() => {
    if (!filterSearch.trim()) return uniqueValues
    const q = filterSearch.toLowerCase().trim()
    return uniqueValues.filter((v) => v.toLowerCase().includes(q))
  }, [uniqueValues, filterSearch])

  // Click outside listener
  useEffect(() => {
    const handleDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.excel-grid-filter-popover')) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleDown)
    return () => document.removeEventListener('mousedown', handleDown)
  }, [onClose])

  // Current sort chain description
  const sortDesc = useMemo(() => {
    return sort
      .map((s) => {
        const c = columns.find((col) => getColKey(col) === s.k)
        return c ? `${getColHeader(c)}${s.dir === 'desc' ? ' ↓' : ' ↑'}` : ''
      })
      .filter(Boolean)
      .join(' › ')
  }, [sort, columns])

  const colKey = getColKey(column)

  const handleSingleSort = (dir: 'asc' | 'desc') => {
    onUpdateSort([{ k: colKey, dir }])
    onClose()
  }

  const handleAddSortLevel = (dir: 'asc' | 'desc') => {
    const existingIdx = sort.findIndex((s) => s.k === colKey)
    let next: SortRule[]
    if (existingIdx >= 0) {
      next = sort.map((s, idx) => (idx === existingIdx ? { k: colKey, dir } : s))
    } else {
      next = [...sort, { k: colKey, dir }]
    }
    onUpdateSort(next)
    onClose()
  }

  const handleClearSort = () => {
    onUpdateSort([])
    onClose()
  }

  const toggleValue = (val: string) => {
    setExcludedSet((prev) => {
      const next = new Set(prev)
      if (next.has(val)) {
        next.delete(val)
      } else {
        next.add(val)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setExcludedSet(new Set())
  }

  const handleClearColumnFilter = () => {
    onApplyFilter([])
    onClose()
  }

  const handleApply = () => {
    onApplyFilter(Array.from(excludedSet))
    onClose()
  }

  // Positioning
  const popoverWidth = 230
  const left = Math.max(10, Math.min(anchorRect.left, window.innerWidth - popoverWidth - 14))
  const top = anchorRect.bottom + 4

  return (
    <div
      className="excel-grid-filter-popover fixed z-[310] bg-white border border-[#777] shadow-[0_4px_14px_rgba(0,0,0,0.3)] p-2 text-[12px] font-sans"
      style={{ left, top, width: popoverWidth }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[11px] text-[#555] mb-1 truncate" title={sortDesc || '(chưa có)'}>
        Thứ tự sắp xếp: {sortDesc || '(chưa có)'}
      </div>

      <div className="flex gap-1 mb-1">
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px] text-center"
          onClick={() => handleSingleSort('asc')}
        >
          A→Z
        </button>
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px] text-center"
          onClick={() => handleSingleSort('desc')}
        >
          Z→A
        </button>
      </div>

      <div className="flex gap-1 mb-1">
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px] text-center text-[11px]"
          onClick={() => handleAddSortLevel('asc')}
        >
          ＋ Cấp A→Z
        </button>
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px] text-center text-[11px]"
          onClick={() => handleAddSortLevel('desc')}
        >
          ＋ Cấp Z→A
        </button>
      </div>

      <div className="mb-2">
        <button
          type="button"
          className="w-full border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px] text-center text-[11px]"
          onClick={handleClearSort}
        >
          Bỏ mọi sắp xếp
        </button>
      </div>

      <input
        type="search"
        className="w-full border border-[#999] px-2 py-1 mb-1 text-[12px] outline-none rounded-[2px]"
        placeholder="Tìm giá trị…"
        value={filterSearch}
        onChange={(e) => setFilterSearch(e.target.value)}
      />

      <div className="max-h-[190px] overflow-auto border border-[#ccc] p-1 mb-2 bg-white">
        {visibleValues.length === 0 ? (
          <div className="text-center text-[#888] py-2 text-[11px]">Không tìm thấy</div>
        ) : (
          visibleValues.map((v) => {
            const isChecked = !excludedSet.has(v)
            return (
              <label
                key={v}
                className="flex items-center gap-1.5 px-1 py-0.5 hover:bg-[#f0f4f8] cursor-pointer text-[12px] truncate"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleValue(v)}
                  className="rounded-[2px]"
                />
                <span className="truncate">{v === '' ? '(Trống)' : v}</span>
              </label>
            )
          })
        )}
      </div>

      <div className="flex gap-1">
        <button
          type="button"
          className="flex-1 border border-[#222] bg-[#222] text-white hover:bg-[#000] py-1 px-2 rounded-[2px] font-semibold"
          onClick={handleApply}
        >
          OK
        </button>
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px]"
          onClick={handleSelectAll}
        >
          Tất cả
        </button>
        <button
          type="button"
          className="flex-1 border border-[#999] bg-white hover:bg-[#eee] py-1 px-1.5 rounded-[2px]"
          onClick={handleClearColumnFilter}
        >
          Bỏ lọc
        </button>
      </div>
    </div>
  )
}
